import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Canvas, useThree, useFrame, useLoader } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { CameraControls, Center, Html, Text, PivotControls } from '@react-three/drei';
import { ModelViewerWrapper, UIOverlay, MapWrapper, MapImage, MapPoint, DebugButton } from './ModelViewer.styled';
import * as THREE from 'three'; 
import { api } from '../../api.js'; 

import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';

const ACTIVAR_VISITA_GUIADA = true;

const ModelViewer = ({
  id, modelPath, collisionPath, maps = [], initialCamera = [0, 0, 0],
  initialLookAt, rotation = [0, 0, 0], position = [0, 0, 0],
  collisionRotation, collisionPosition, testingMode = false, orbitMode = false,
  onLoaded
}) => {
  
  const cameraControlRef = useRef();
  const collisionMeshRef = useRef(); 
  
  const [currentMapIndex, setCurrentMapIndex] = useState(0);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const tooltipRef = useRef(null);
  const handleHover = useCallback((title, x, y) => {
    const el = tooltipRef.current;
    if (!el) return;
    if (!title) {
      el.style.display = 'none';
    } else {
      el.textContent = title;
      el.style.left = `${x + 14}px`;
      el.style.top = `${y - 10}px`;
      el.style.display = 'block';
    }
  }, []);
  const activeMap = (maps && maps.length > 0) ? maps[currentMapIndex] : null;

  const [isWalking, setIsWalking] = useState(false);
  const [heightOffset, setHeightOffset] = useState(0);

  const [isLoaded, setIsLoaded] = useState(false);

  const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(true);

  // ESTADOS PARA ANOTACIONES SQL
  const [isAddingAnnotation, setIsAddingAnnotation] = useState(false);
  const [customAnnotations, setCustomAnnotations] = useState([]);
  const [pendingAnnotationPos, setPendingAnnotationPos] = useState(null);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [annotationMode, setAnnotationMode] = useState("text"); 
  const [isAnnotationListOpen, setIsAnnotationListOpen] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  const loadSQLAnnotations = async () => {
    try {
      const data = await api.getAnnotations();
      setCustomAnnotations(data || []);
    } catch (e) {
      console.error("Error cargando anotaciones SQL:", e);
    }
  };

  useEffect(() => { loadSQLAnnotations(); }, []);

  useEffect(() => {
    document.body.style.cursor = isAddingAnnotation && !pendingAnnotationPos ? 'crosshair' : 'auto';
    return () => { document.body.style.cursor = 'auto'; };
  }, [isAddingAnnotation, pendingAnnotationPos]);

  const allAnnotations = useMemo(() => {
    const fixedAnnotations = (activeMap?.annotations || []).map((a, i) => ({ 
      ...a, id: `fixed-${i}`, isFixed: true 
    }));
    return [...fixedAnnotations, ...customAnnotations];
  }, [activeMap, customAnnotations]);

  const visibleAnnotations = useMemo(() => {
    return allAnnotations.filter(note => !deletedIds.includes(note.id));
  }, [allAnnotations, deletedIds]);

  // Cuando los datos del backend actualizan el contenido de las anotaciones fijas,
  // sincronizamos el modal abierto para que muestre los valores nuevos en tiempo real.
  useEffect(() => {
    if (!activeAnnotation?.id?.startsWith('fixed-')) return;
    const updated = allAnnotations.find(a => a.id === activeAnnotation.id);
    if (updated && updated.content !== activeAnnotation.content) {
      setActiveAnnotation(prev => ({ ...prev, content: updated.content }));
    }
  }, [allAnnotations]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsLoaded(false); // Reseteamos si cambia la calidad
  }, [modelPath]);

  const walkRef = useRef({ active: false, pathQueue: [], currentStart: null, currentVelocity: null });
  const WALK_SPEED = 1.6;

  const currentPointIndex = activeMap ? activeMap.points.findIndex(p => p.label === currentLocation) : 0;
  const totalPoints = activeMap ? activeMap.points.length : 0;

  const handleTourNavigate = (direction) => {
    if (!activeMap || isWalking) return;
    const newIndex = currentPointIndex + direction;
    if (newIndex < 0 || newIndex >= totalPoints) return;
    
    const targetPoint = activeMap.points[newIndex];
    const rutaCompleta = calcularRutaCompleta(targetPoint.label);
    handleLocationChange(targetPoint.label, targetPoint.camPosition, rutaCompleta || []);
  };

  const handleLocationChange = (targetLabel, finalCoords, path = []) => {
    if (walkRef.current.active) return;
    setActiveAnnotation(null);
    setCurrentLocation(targetLabel);
    setIsWalking(true);

    const destPoint = activeMap.points.find(p => p.label === targetLabel);
    const destLookAt = destPoint && destPoint.camLookAt ? new THREE.Vector3(...destPoint.camLookAt) : null;

    const startPos = cameraControlRef.current.camera.position.clone();
    const pathVectors = path.map(p => new THREE.Vector3(p[0], p[1], p[2]));
    const endPos = new THREE.Vector3(finalCoords[0], finalCoords[1], finalCoords[2]);
    pathVectors.push(endPos);

    walkRef.current = { 
      active: true, pathQueue: pathVectors, currentStart: startPos, 
      currentVelocity: null, destLookAt: destLookAt, isTurning: false 
    };
  };

  const calcularRutaCompleta = (destinoLabel) => {
    if (!activeMap || !activeMap.navigation || !currentLocation || currentLocation === destinoLabel) return null;
    const grafo = {};
    activeMap.navigation.forEach(nav => {
      if (!grafo[nav.visibleFrom]) grafo[nav.visibleFrom] = [];
      grafo[nav.visibleFrom].push({ target: nav.targetLabel, path: nav.path || [], fullNav: nav });
    });

    const cola = [[currentLocation]];
    const visitados = new Set([currentLocation]);
    const mapaPadres = {};
    let encontrado = false;

    while (cola.length > 0) {
      const caminoActual = cola.shift();
      const nodoActual = caminoActual[caminoActual.length - 1];
      if (nodoActual === destinoLabel) { encontrado = true; break; }
      if (grafo[nodoActual]) {
        for (const vecino of grafo[nodoActual]) {
          if (!visitados.has(vecino.target)) {
            visitados.add(vecino.target);
            mapaPadres[vecino.target] = { padre: nodoActual, datosConexion: vecino };
            cola.push([...caminoActual, vecino.target]);
          }
        }
      }
    }

    if (!encontrado) return null;

    let paso = destinoLabel;
    const pathTotal = [];
    const pasosInversos = [];
    while (paso !== currentLocation) {
      const info = mapaPadres[paso];
      if (!info) break;
      pasosInversos.unshift(info.datosConexion);
      paso = info.padre;
    }

    pasosInversos.forEach((tramo, index) => {
      if (tramo.path) pathTotal.push(...tramo.path);
      if (index < pasosInversos.length - 1) {
        const puntoIntermedio = activeMap.points.find(p => p.label === tramo.target);
        if (puntoIntermedio && puntoIntermedio.camPosition) {
          pathTotal.push([puntoIntermedio.camPosition[0], puntoIntermedio.camPosition[1], puntoIntermedio.camPosition[2]]);
        }
      }
    });

    return pathTotal;
  };

  useEffect(() => {
    setCurrentMapIndex(0);
    if (maps[0] && maps[0].points && maps[0].points.length > 0) {
      setCurrentLocation(maps[0].points[0].label);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const initialCameraStr = initialCamera?.join(',');
  const initialLookAtStr = initialLookAt?.join(',');

  useEffect(() => {
    if (isLoaded && cameraControlRef.current) {
      const [x, y, z] = initialCamera;
      let targetX = initialLookAt?.[0] || 0, targetY = initialLookAt?.[1] || 0, targetZ = initialLookAt?.[2] || 0;

      if (orbitMode) {
          cameraControlRef.current.setLookAt(x, y, z, targetX, targetY, targetZ, false);
      } else {
          const posVec = new THREE.Vector3(x, y, z);
          const lookAtVec = new THREE.Vector3(targetX, targetY, targetZ);
          if (posVec.distanceTo(lookAtVec) < 0.001) lookAtVec.add(new THREE.Vector3(0, 0, -1));
          const direction = new THREE.Vector3().subVectors(lookAtVec, posVec).normalize();
          const targetCercano = posVec.clone().add(direction.multiplyScalar(0.1));

          cameraControlRef.current.setLookAt(posVec.x, posVec.y, posVec.z, targetCercano.x, targetCercano.y, targetCercano.z, false);
      }
      cameraControlRef.current.update(0);
    }
  }, [isLoaded, initialCameraStr, initialLookAtStr, orbitMode]);

  if (!modelPath) return null;

  const logCoords = () => {
    if (cameraControlRef.current) {
      const pos = cameraControlRef.current.camera.position;
      console.log(`COPIAR: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
    }
  };

  const MOSTRAR_FLECHAS = false; 

  return (
    <>
      {createPortal(
        <LoadingScreen isLoaded={isLoaded} />,
        document.body
      )}
      <ModelViewerWrapper data-testid="ModelViewer">
        <Canvas flat gl={{ antialias: false }} camera={{ position: initialCamera, fov: 60 }} dpr={1}>
          <color attach="background" args={['#000000']} />
          
          <group position={[0, -heightOffset, 0]}>
            <Center>
              <Suspense fallback={null}>
                <SplatModel 
                  src={modelPath} 
                  rotation={rotation} 
                  position={position} 
                  onLoaded={() => {
                    setIsLoaded(true);
                    if (onLoaded) onLoaded();
                  }} 
                />
              </Suspense>
            </Center>

            {testingMode && activeMap && <PathVisualizer navigation={activeMap.navigation} />}

            {collisionPath && (
              <CollisionMesh 
                ref={collisionMeshRef}
                url={collisionPath} 
                cameraControlRef={cameraControlRef} 
                testingMode={testingMode} 
                rotation={collisionRotation || rotation} 
                position={collisionPosition || position} 
                onClick={(e) => {
                  if (isAddingAnnotation && !pendingAnnotationPos) {
                    e.stopPropagation();
                    const DISTANCIA_SEPARACION = 0.25;
                    const direccionHaciaCamara = e.camera.position.clone().sub(e.point).normalize();
                    const puntoSeparado = e.point.clone().add(direccionHaciaCamara.multiplyScalar(DISTANCIA_SEPARACION));
                    const localPoint = e.eventObject.worldToLocal(puntoSeparado);
                    setPendingAnnotationPos([localPoint.x, localPoint.y, localPoint.z]);
                  }
                }}
              />
            )}

            <IframeWall 
              url="https://nextmachina.duckdns.org/" 
              position={[-2, 0.15, -1.81]}
              rotation={[0, (2 * Math.PI / 180), 0]}
              scale={0.04}
            />

            {visibleAnnotations.map((note, index) => (
              <InfoLabel
                key={note.id || index} position={note.position} title={note.title} content={note.content} isHtml={note.isHtml}
                icon={note.icon} maxDistance={note.maxDistance} maxXDistance={note.maxXDistance}
                maxYDistance={note.maxYDistance} maxZDistance={note.maxZDistance} scale={note.scale} maxWidth={note.maxWidth}
                testingMode={testingMode}
                onOpen={(data) => setActiveAnnotation({ ...note, ...data })}
                collisionMeshRef={collisionMeshRef}
                onHover={handleHover}
              />
            ))}

            {MOSTRAR_FLECHAS && activeMap && activeMap.navigation && activeMap.navigation.map((nav, index) => {
              if (!testingMode && nav.visibleFrom !== currentLocation) return null;
              const foundPoint = activeMap.points.find(p => p.label === nav.targetLabel);
              const coordsDestino = foundPoint ? foundPoint.camPosition : nav.target;
              if (!coordsDestino) return null;

              return (
                <NavArrow key={`nav-${index}`} position={nav.position} rotationY={nav.rotationY || 0} label={nav.label} onClick={() => handleLocationChange(nav.targetLabel, coordsDestino, nav.path)} />
              );
            })}
          </group>

          <CameraTracker cameraControlRef={cameraControlRef} />
          <AnimationLoop walkRef={walkRef} cameraControlRef={cameraControlRef} setIsWalking={setIsWalking} WALK_SPEED={WALK_SPEED} />

          <CameraControls 
            ref={cameraControlRef} makeDefault 
            dollySpeed={orbitMode ? 1 : (testingMode ? 1 : 0)} truckSpeed={orbitMode ? -1 : (testingMode ? -1.5 : 0)}
            azimuthRotateSpeed={testingMode || orbitMode ? -1 : -0.3} polarRotateSpeed={testingMode || orbitMode ? -1 : -0.3} 
            minDistance={orbitMode ? 1.5 : 0} maxDistance={orbitMode ? 20 : Infinity}
          />
          {testingMode && <CameraLogger />}
        </Canvas>
      </ModelViewerWrapper>

      <UIOverlay style={{ opacity: isLoaded ? 1 : 0, pointerEvents: 'none', transition: 'opacity 0.6s ease-in' }}>
        {testingMode && <DebugButton onClick={logCoords} style={{ pointerEvents: 'auto' }}>DEBUG: LOG COORDENADAS</DebugButton>}

        <div style={{ position: 'absolute', top: '70px', right: '20px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end', pointerEvents: isLoaded ? 'auto' : 'none', zIndex: 10000 }}>
          <button
            onClick={() => { setIsAddingAnnotation(!isAddingAnnotation); setPendingAnnotationPos(null); }}
            style={{
              padding: '8px 16px', borderRadius: '20px', background: isAddingAnnotation ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.6)', 
              color: 'white', border: '1px solid rgba(255, 255, 255, 0.4)', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.4)', backdropFilter: 'blur(5px)', transition: 'all 0.2s ease-in-out'
            }}
          >
            {isAddingAnnotation ? 'Cancelar' : '📍 Añadir Anotación'}
          </button>

          <button
            onClick={() => setIsAnnotationListOpen(!isAnnotationListOpen)}
            style={{
              padding: '8px 16px', borderRadius: '20px', background: isAnnotationListOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.6)', 
              color: 'white', border: '1px solid rgba(255, 255, 255, 0.4)', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.4)', backdropFilter: 'blur(5px)', transition: 'all 0.2s ease-in-out'
            }}
          >
            📋 Anotaciones ({visibleAnnotations.length})
          </button>

          {isAnnotationListOpen && (
            <div style={{
              background: 'rgba(0, 0, 0, 0.6)', border: '1px solid rgba(255, 255, 255, 0.2)', padding: '15px', borderRadius: '12px', width: '280px', color: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto', backdropFilter: 'blur(5px)'
            }}>
              <h4 style={{ margin: 0, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', fontSize: '15px' }}>Lista de Anotaciones</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {visibleAnnotations.length === 0 ? (
                  <span style={{ fontSize: '13px', color: '#aaa' }}>No hay anotaciones visibles.</span>
                ) : (
                  visibleAnnotations.map(note => {
                    const isImageIcon = note.icon && note.icon.includes('.');
                    return (
                      <div key={note.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 10px', borderRadius: '6px' }}>
                        <a 
                          href="#" onClick={(e) => { e.preventDefault(); setActiveAnnotation(note); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#3cccf4', textDecoration: 'none', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: note.isFixed ? '230px' : '190px' }}
                        >
                          {isImageIcon ? <img src={note.icon} alt="icon" style={{ width: '16px', height: '16px', objectFit: 'contain' }} /> : <span>{note.icon}</span>}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{note.title}</span>
                        </a>
                        
                        {!note.isFixed && (
                          <button 
                            onClick={async () => {
                              if (window.confirm("¿Seguro que quieres borrar esta anotación?")) {
                                await api.deleteAnnotation(note.id);
                                setDeletedIds(prev => [...prev, note.id]);
                                if (activeAnnotation && activeAnnotation.id === note.id) setActiveAnnotation(null);
                                loadSQLAnnotations();
                              }
                            }}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }} 
                            title="Borrar anotación"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{
          position: 'absolute', top: '90px', left: '20px', pointerEvents: isLoaded ? 'auto' : 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', zIndex: 99999 
        }}>
          <button
            onClick={() => setIsDashboardExpanded(!isDashboardExpanded)}
            title={isDashboardExpanded ? "Ocultar Dashboard" : "Mostrar Dashboard"}
            style={{
              width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(0, 0, 0, 0.8)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.4)', cursor: 'pointer',
              display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '18px', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', transition: 'background 0.2s, transform 0.2s',
            }}
          >📊</button>

          {isDashboardExpanded && (
            <div style={{
              background: 'rgba(0, 0, 0, 0.6)', padding: '20px', borderRadius: '12px', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)', width: '250px', display: 'flex', flexDirection: 'column', gap: '15px'
            }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '18px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>Dashboard: Ansareo</h3>
              <a href="https://nextmachina.duckdns.org/" target="_blank" rel="noreferrer" style={{ color: '#3cccf4', textDecoration: 'none', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><span>🔗</span> Informe de control</a>
              <a href="/assets/images/annotations/Bombeo Castrejana.pdf" target="_blank" rel="noreferrer" style={{ color: '#3cccf4', textDecoration: 'none', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><span>📄</span> Ficha técnica</a>
              <div style={{ color: '#e2e8f0', fontSize: '14px', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px' }}><span>🕒</span> Histórico de mantenimientos</div>
            </div>
          )}
        </div>

        {/* SLIDER DE ALTURA CON PRESETS */}
        <div style={{
          position: 'absolute', bottom: '20px', left: '20px', pointerEvents: isLoaded ? 'auto' : 'none',
          background: 'rgba(0, 0, 0, 0.6)', padding: '16px 16px', borderRadius: '12px', backdropFilter: 'blur(5px)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.15)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px'
        }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>
            <span>Altura</span><br/>
            <span style={{ color: 'white' }}>{(heightOffset + 1).toFixed(2)}m</span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '15px' }}>
            {/* Barra vertical */}
            <input 
              type="range" min="-1" max="0.5" step="0.05" value={heightOffset}
              onChange={(e) => setHeightOffset(parseFloat(e.target.value))}
              onPointerDown={(e) => e.stopPropagation()} 
              style={{ cursor: 'pointer', accentColor: 'white', WebkitAppearance: 'slider-vertical', appearance: 'slider-vertical', width: '8px', height: '100px' }}
            />
            
            {/* Columna de Presets */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100px' }}>
              <button 
                onClick={(e) => { e.stopPropagation(); setHeightOffset(0.5); }}
                onPointerDown={(e) => e.stopPropagation()}
                title="Alto (1.50m)"
                style={{
                  width: '28px', height: '28px', borderRadius: '50%', padding: 0, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center',
                  background: Math.abs(heightOffset - 0.5) < 0.01 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                  border: Math.abs(heightOffset - 0.5) < 0.01 ? '1px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >🧑</button>
              
              <button 
                onClick={(e) => { e.stopPropagation(); setHeightOffset(0.0); }}
                onPointerDown={(e) => e.stopPropagation()}
                title="Medio (1.00m)"
                style={{
                  width: '28px', height: '28px', borderRadius: '50%', padding: 0, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center',
                  background: Math.abs(heightOffset - (0.0)) < 0.01 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                  border: Math.abs(heightOffset - (0.0)) < 0.01 ? '1px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >👦</button>
              
              <button 
                onClick={(e) => { e.stopPropagation(); setHeightOffset(-1); }}
                onPointerDown={(e) => e.stopPropagation()}
                title="Bajo (0.00m)"
                style={{
                  width: '28px', height: '28px', borderRadius: '50%', padding: 0, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center',
                  background: Math.abs(heightOffset - (-1)) < 0.01 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                  border: Math.abs(heightOffset - (-1)) < 0.01 ? '1px solid white' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >🐕</button>
            </div>
          </div>
        </div>

        {ACTIVAR_VISITA_GUIADA && activeMap && totalPoints > 1 && (
          <div style={{
            position: 'absolute', bottom: '25px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '15px', zIndex: 2000, pointerEvents: isLoaded ? 'auto' : 'none'
          }}>
            <button
              onClick={(e) => { e.stopPropagation(); handleTourNavigate(-1); }}
              onPointerDown={(e) => e.stopPropagation()} disabled={currentPointIndex === 0 || isWalking}
              style={{
                width: '120px', justifyContent: 'center',
                padding: '8px 16px', background: 'rgba(0, 0, 0, 0.8)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', backdropFilter: 'blur(5px)', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: '8px',
                opacity: (currentPointIndex === 0 || isWalking) ? 0.3 : 1, cursor: (currentPointIndex === 0 || isWalking) ? 'not-allowed' : 'pointer',
              }}
            >⬅ Anterior</button>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', fontFamily: 'sans-serif', background: 'rgba(0,0,0,0.6)', padding: '4px 12px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.2)', minWidth: '60px', textAlign: 'center', backdropFilter: 'blur(5px)', boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
            }}>{currentPointIndex + 1} / {totalPoints}</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleTourNavigate(1); }}
              onPointerDown={(e) => e.stopPropagation()} disabled={currentPointIndex === totalPoints - 1 || isWalking}
              style={{
                width: '120px', justifyContent: 'center',
                padding: '8px 16px', background: 'rgba(0, 0, 0, 0.8)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', backdropFilter: 'blur(5px)', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: '8px',
                opacity: (currentPointIndex === totalPoints - 1 || isWalking) ? 0.3 : 1, cursor: (currentPointIndex === totalPoints - 1 || isWalking) ? 'not-allowed' : 'pointer',
              }}
            >Siguiente ➡</button>
          </div>
        )}

        {activeMap && (
          <MapWrapper style={{ pointerEvents: isLoaded ? 'auto' : 'none', position: 'relative' }}>
            <div style={{ display: isMapExpanded ? 'block' : 'none' }}>
              {maps.length > 1 && (
                <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                  <select 
                    value={currentMapIndex} onChange={(e) => setCurrentMapIndex(Number(e.target.value))}
                    style={{ padding: '5px', borderRadius: '5px', width: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.2)'}}
                  >
                    {maps.map((m, i) => <option key={i} value={i}>{m.name}</option>)}
                  </select>
                </div>
              )}
              {activeMap.image && (
                <div style={{ position: 'relative', background: 'rgba(0, 0, 0, 0.6)', borderRadius: '12px', backdropFilter: 'blur(5px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                  <MapImage src={activeMap.image} alt={activeMap.name} />
                  {activeMap.points.map((punto, index) => (
                    <MapPoint 
                      key={index} $top={punto.top} $left={punto.left} data-label={punto.label}
                      onClick={() => {
                        if (currentLocation === punto.label) return;
                        const rutaCompleta = calcularRutaCompleta(punto.label);
                        if (rutaCompleta) handleLocationChange(punto.label, punto.camPosition, rutaCompleta);
                        else handleLocationChange(punto.label, punto.camPosition, []); 
                      }}
                      style={{ backgroundColor: currentLocation === punto.label ? '#148f14' : '', cursor: currentLocation === punto.label ? 'default' : 'pointer' }}
                    >
                      i
                      {currentLocation === punto.label && !isWalking && (
                        <div id="active-fov-cone" style={{
                          position: 'absolute', top: '50%', left: '50%', width: '80px', height: '80px',
                          background: `conic-gradient(from 315deg, rgba(20, 143, 20, 0.4) 0deg, rgba(20, 143, 20, 0.4) 90deg, transparent 90deg)`, 
                          borderRadius: '50%', transformOrigin: 'center center', transform: 'translate(-50%, -50%) rotate(0deg)', pointerEvents: 'none', zIndex: -1
                        }}/>
                      )}
                    </MapPoint>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setIsMapExpanded(!isMapExpanded)} title={isMapExpanded ? "Ocultar mapa" : "Mostrar mapa"}
              style={{
                position: isMapExpanded ? 'absolute' : 'relative', bottom: isMapExpanded ? '-12px' : '0', right: isMapExpanded ? '-12px' : '0', width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(0, 0, 0, 0.8)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.4)', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '18px', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 10
              }}
            >{isMapExpanded ? '>>' : '<<'}</button>
          </MapWrapper>
        )}
      </UIOverlay>

      {pendingAnnotationPos && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(3px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'auto'
        }}>
          <div style={{
            background: 'rgba(20, 20, 20, 0.95)', border: '1px solid rgba(255, 255, 255, 0.2)', padding: '25px', borderRadius: '16px',
            width: '380px', color: 'white', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: '15px'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>Nueva Anotación</h3>
            
            <input 
              type="text" placeholder="Título de la anotación..." value={newNoteTitle} onChange={(e) => setNewNoteTitle(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px', borderRadius: '8px', outline: 'none', fontSize: '15px' }}
            />

            <textarea 
              placeholder={annotationMode === 'text' ? "Escribe el contenido aquí..." : "Pega tu código HTML, <iframe> o widget aquí..."} 
              value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} rows={5}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px', borderRadius: '8px', outline: 'none', fontSize: '14px', resize: 'vertical', fontFamily: annotationMode === 'html' ? 'monospace' : 'inherit' }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
              <button 
                onClick={() => setAnnotationMode(prev => prev === 'text' ? 'html' : 'text')}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', color: '#3cccf4', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >{annotationMode === 'text' ? '✏️ Escribiendo nota' : '🌐 Embebiendo HTML'}</button>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => { setPendingAnnotationPos(null); setNewNoteTitle(""); setNewNoteContent(""); setAnnotationMode("text"); setIsAddingAnnotation(false); }}
                  style={{ background: 'transparent', border: '1px solid #dc3545', color: '#dc3545', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >Cancelar</button>
                <button 
                  onClick={async () => {
                    const titleToSave = newNoteTitle.trim() || (annotationMode === 'html' ? "Widget" : "Anotación Manual");
                    const contentToSave = newNoteContent.trim() || "Sin contenido añadido.";
                    const newAnnotation = {
                      id: `custom-${Date.now()}`, title: titleToSave, content: contentToSave, isHtml: annotationMode === 'html', 
                      icon: annotationMode === 'html' ? "🌐" : "📝", position: pendingAnnotationPos, maxDistance: 50, maxXDistance: 20, maxYDistance: 20, maxZDistance: 20, scale: 1
                    };
                    await api.saveAnnotation(newAnnotation);
                    loadSQLAnnotations(); 
                    setPendingAnnotationPos(null); setNewNoteTitle(""); setNewNoteContent(""); setAnnotationMode("text"); setIsAddingAnnotation(false); 
                  }}
                  style={{ background: '#28a745', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={tooltipRef} style={{
        display: 'none',
        position: 'fixed',
        background: 'rgba(15,15,20,0.88)',
        color: '#fff',
        fontSize: '11px',
        fontFamily: 'sans-serif',
        padding: '4px 9px',
        borderRadius: '5px',
        whiteSpace: 'nowrap',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
        zIndex: 99999,
      }} />

      <AnnotationModal data={activeAnnotation} onClose={() => setActiveAnnotation(null)} />
    </>
  );
};

// ==========================================
// COMPONENTES SECUNDARIOS
// ==========================================

const SplatModel = ({ src, rotation, position, onLoaded }) => {
  const [viewer, setViewer] = useState(null);

  // Guardamos la función en una referencia para que React 
  // no cancele y reinicie la descarga del modelo cada vez que haces clic en la interfaz.
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => { onLoadedRef.current = onLoaded; }, [onLoaded]);

  useEffect(() => {
    let isMounted = true;

    const dropInViewer = new GaussianSplats3D.DropInViewer({
      dynamicScene: false,
      sharedMemoryForWorkers: false // Aumenta la compatibilidad y previene cuelgues
    });

    dropInViewer.addSplatScene(src, {
      showLoadingUI: false, 
      format: 0
    }).then(() => {
      if (isMounted && onLoadedRef.current) onLoadedRef.current();
    }).catch((err) => {
      // Si React interrumpe la carga (por ejemplo al cambiar de calidad), 
      // atrapamos el error en silencio sin ensuciar la consola.
      if (isMounted) console.warn("Carga de Splat cancelada:", err.message);
    });

    setViewer(dropInViewer);

    return () => {
      isMounted = false;
      dropInViewer.dispose();
    };
  }, [src]);

  // 2. FIX DEL TECHO: Envolvemos el visor en un grupo con tu posición y rotación, 
  // y le aplicamos un scale=[1, -1, -1] exactamente igual que hacía la librería vieja.
  return viewer ? (
    <group position={position} rotation={rotation}>
      <primitive object={viewer} scale={[1, -1, -1]} dispose={null} />
    </group>
  ) : null;
};

// PANTALLA DE CARGA
const LoadingScreen = ({ isLoaded }) => {
  return (
    <div style={{ 
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', 
      background: '#000000', display: 'flex', flexDirection: 'column', 
      alignItems: 'center', justifyContent: 'center', zIndex: 9999999, 
      transition: 'opacity 0.6s ease-out', opacity: isLoaded ? 0 : 1, 
      pointerEvents: isLoaded ? 'none' : 'all' 
    }}>
      <div style={{ fontSize: '24px', color: 'white', fontWeight: 'bold', marginBottom: '20px', letterSpacing: '2px', textAlign: 'center' }}>
        CARGANDO ENTORNO 3D
      </div>
      <div style={{ color: '#aaa', fontSize: '14px', fontStyle: 'italic' }}>
        Procesando gráficos nativos...
      </div>
    </div>
  );
};

const IframeWall = ({ url, position, rotation, width = 1024, height = 768, scale = 0.002 }) => {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <Html transform occlude="blending">
        <div style={{ width: `${width}px`, height: `${height}px`, background: '#000', border: '12px solid #dfdfdf', boxShadow: '0 0 40px rgba(170, 172, 172, 0.6)', overflow: 'hidden', display: 'flex' }}>
          <iframe src={url} style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'auto' }} title="Pantalla-Embebida" />
        </div>
      </Html>
    </group>
  );
};

const CameraTracker = ({ cameraControlRef }) => {
  useFrame(() => {
    if (cameraControlRef.current) {
      const azimuth = cameraControlRef.current.azimuthAngle;
      const angleDeg = THREE.MathUtils.radToDeg(azimuth);
      const cone = document.getElementById('active-fov-cone');
      if (cone) cone.style.transform = `translate(-50%, -50%) rotate(${-angleDeg}deg)`;
    }
  });
  return null;
};

const NavArrow = ({ position, rotationY, onClick, label }) => {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'auto';
    return () => { document.body.style.cursor = 'auto'; }
  }, [hovered]);

  return (
    <group position={position} rotation={[-Math.PI / 2, 0, rotationY]} onClick={(e) => { e.stopPropagation(); onClick(); }} onPointerOver={() => setHovered(true)} onPointerOut={() => setHovered(false)}>
      <mesh>
        <circleGeometry args={[0.4, 32]} />
        <meshBasicMaterial color={hovered ? "#61dafb" : "white"} transparent opacity={hovered ? 0.8 : 0.4} side={THREE.DoubleSide} />
      </mesh>
      <Text position={[0, 0, 0.05]} fontSize={0.4} color={hovered ? "black" : "black"} anchorX="center" anchorY="middle" rotation={[0, 0, -Math.PI / 2]}>➜</Text>
      {hovered && label && (
        <Html position={[0, 0.5, 0]} center>
           <div style={{ background: 'rgba(0,0,0,0.8)', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
             {label}
           </div>
        </Html>
      )}
    </group>
  );
};

const InfoLabel = React.memo(({
  position, title, content, isHtml = false, icon = "📌", scale = 1, maxWidth = "600px", testingMode = false, onOpen, collisionMeshRef,
  maxDistance = 3, maxYDistance = 5, maxXDistance = 5, maxZDistance = 5, onHover
}) => {
  const { camera } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const markerRef = useRef();
  const frameRef = useRef(0);

  const dragPosRef = useRef(new THREE.Vector3(0, 0, 0));

  const [isVisible, setIsVisible] = useState(false);
  const [debugHitInfo, setDebugHitInfo] = useState("");
  const [imageTexture, setImageTexture] = useState(null);
  const isImage = icon.includes('.'); 
  
  useEffect(() => {
    if (isImage) {
      new THREE.TextureLoader().load(icon, (tex) => {
        if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
        setImageTexture(tex);
      });
    }
  }, [icon, isImage]);

  const emojiTexture = useMemo(() => {
    if (isImage) return null; 
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 128;
    const ctx = canvas.getContext('2d');
    ctx.font = '90px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(icon, 64, 70); 
    const texture = new THREE.CanvasTexture(canvas);
    if (THREE.SRGBColorSpace) texture.colorSpace = THREE.SRGBColorSpace; 
    return texture;
  }, [icon, isImage]);

  const activeTexture = isImage ? imageTexture : emojiTexture;

  useFrame(() => {
    if (!markerRef.current) return;

    // Billboard: siempre, barato
    if (!testingMode) markerRef.current.quaternion.copy(camera.quaternion);

    // Visibilidad + raycasts: solo cada 6 frames (~10 veces/s) para ahorrar CPU
    frameRef.current++;
    if (frameRef.current % 12 !== 0) return;

    const worldPos = new THREE.Vector3();
    markerRef.current.getWorldPosition(worldPos);
    const dist = camera.position.distanceTo(worldPos);

    const checkNearby = dist < maxDistance &&
      Math.abs(camera.position.y - worldPos.y) < maxYDistance &&
      Math.abs(camera.position.x - worldPos.x) < maxXDistance &&
      Math.abs(camera.position.z - worldPos.z) < maxZDistance;

    let checkVisible = checkNearby;
    let hitText = "";

    if (checkNearby && collisionMeshRef?.current) {
      const radius = 0.1 * scale;
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion).multiplyScalar(radius);
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).multiplyScalar(radius);
      const testPoints = [
        worldPos,
        worldPos.clone().add(right),
        worldPos.clone().sub(right),
        worldPos.clone().add(up),
        worldPos.clone().sub(up)
      ];
      for (let i = 0; i < testPoints.length; i++) {
        const pt = testPoints[i];
        const direction = new THREE.Vector3().subVectors(pt, camera.position);
        const distanceToPt = direction.length();
        direction.normalize();
        raycaster.current.set(camera.position, direction);
        const intersects = raycaster.current.intersectObject(collisionMeshRef.current, true);
        const choquesValidos = intersects.filter(hit => hit.distance > 0.3);
        if (choquesValidos.length > 0 && choquesValidos[0].distance < distanceToPt - 0.15) {
          checkVisible = false;
          hitText = `Muro tapando borde`;
          break;
        }
      }
    }

    if (isVisible !== checkVisible) setIsVisible(checkVisible);
    if (debugHitInfo !== hitText) setDebugHitInfo(hitText);
  });

  const handleDrag = (localMatrix) => {
    dragPosRef.current.setFromMatrixPosition(localMatrix);
  };

  const handleDragEnd = () => {
    const delta = dragPosRef.current;
    const finalX = position[0] + delta.x;
    const finalY = position[1] + delta.y;
    const finalZ = position[2] + delta.z;
    console.log(`📋 Copiar para App.js en ${title}: [${finalX.toFixed(2)}, ${finalY.toFixed(2)}, ${finalZ.toFixed(2)}]`);
  };

  const debugColor = isVisible ? "#22c55e" : "#eab308";
  const debugText = isVisible ? title : (debugHitInfo ? `OCULTO: ${debugHitInfo}` : `LEJOS`);

  return (
    <PivotControls 
      enabled={testingMode} 
      activeAxes={[true, true, true]} 
      lineWidth={testingMode ? 3 : 0} 
      axisColors={['#ff4d4d', '#4dff4d', '#4d4dff']} 
      fixed={true} 
      scale={70} 
      depthTest={false} 
      anchor={[0, 0, 0]} 
      autoTransform={true} 
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
    >
      <group ref={markerRef} position={position} frustumCulled={false}>
        {testingMode && (
          <mesh frustumCulled={false}>
            <sphereGeometry args={[0.1 * scale, 16, 16]} /> 
            <meshBasicMaterial color={debugColor} wireframe={true} transparent opacity={0.6} />
             <Html position={[0, 0.15 * scale, 0]} center zIndexRange={[100, 0]}>
              <div style={{ color: debugColor, fontWeight:'bold', fontSize: '11px', whiteSpace: 'nowrap', textShadow: '0 0 4px black', pointerEvents: 'none' }}>
                {debugText}
              </div>
            </Html>
          </mesh>
        )}

        {(!testingMode && isVisible) && (
          <group frustumCulled={false}>
            <mesh
              onClick={(e) => { e.stopPropagation(); onOpen({ title, content, maxWidth, isHtml }); }}
              onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; onHover?.(title, e.clientX, e.clientY); }}
              onPointerOut={() => { document.body.style.cursor = 'auto'; onHover?.(null); }}
              frustumCulled={false}
            >
              <sphereGeometry args={[0.16 * scale]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>

            {activeTexture && (
              <mesh frustumCulled={false}>
                <planeGeometry args={[0.18 * scale, 0.18 * scale]} />
                <meshBasicMaterial map={activeTexture} transparent={true} alphaTest={0.1} depthWrite={true} side={THREE.DoubleSide} />
              </mesh>
            )}
          </group>
        )}
      </group>
    </PivotControls>
  );
});

const AnnotationModal = ({ data, onClose }) => {
  const [lastData, setLastData] = useState(null);
  useEffect(() => { if (data) setLastData(data); }, [data]);
  const stopEvents = (e) => e.stopPropagation();

  if (!data && !lastData) return null;
  const activeData = data || lastData;
  const isOpen = !!data;

  return (
    <div 
      onClick={stopEvents} onPointerDown={stopEvents} onWheel={stopEvents}
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0, 0, 0, 0.3)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center',
        visibility: isOpen ? 'visible' : 'hidden', opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none', transition: 'all 0.3s ease'
      }}
    >
      <style>{`
        .contenido-anotacion a, .contenido-anotacion a:link, .contenido-anotacion a:visited, .contenido-anotacion a:hover, .contenido-anotacion a:active {
          color: #3cccf4 !important; text-decoration: underline;
        }
      `}</style>
      
      <div style={{
        position: 'relative', background: 'rgba(0, 0, 0, 0.6)', border: '1px solid rgba(255,255,255,0.15)',
        padding: '40px', borderRadius: '16px', color: 'white', maxWidth: activeData.maxWidth || '600px', width: '90%', 
        boxShadow: '0 20px 50px rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)',
        transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}>
        <button 
          onClick={onClose}
          style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 100000, background: 'none', border: 'none', color: '#aaa', fontSize: '24px', cursor: 'pointer', transition: 'transform 0.2s, color 0.2s' }}
          onMouseEnter={(e) => { e.target.style.transform = 'scale(1.2)'; e.target.style.color = 'white'; }}
          onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.color = '#aaa'; }}
        >✖</button>

        {activeData.title && (
          <h2 style={{ color: 'white', marginTop: 0, fontSize: '28px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px', marginBottom: '20px', paddingRight: '30px' }}>
            {activeData.title}
          </h2>
        )}
        <div className="contenido-anotacion" style={{ lineHeight: '1.8', fontSize: '18px', color: '#e2e8f0' }}>
          {activeData.isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: activeData.content }} />
          ) : (
            activeData.content
          )}
        </div>
      </div>
    </div>
  );
};

const PathVisualizer = ({ navigation }) => {
  if (!navigation) return null;
  return (
    <group>
      {navigation.map((nav, i) => (
        <group key={`path-group-${i}`}>
          {nav.path && nav.path.map((point, j) => (
            <mesh key={`p-${i}-${j}`} position={[point[0], point[1], point[2]]}>
              <sphereGeometry args={[0.08, 16, 16]} /> 
              <meshBasicMaterial color="#FFFF00" wireframe={true} />
              <Html position={[0, 0.2, 0]} center>
                <div style={{ color: 'yellow', fontSize: '10px', background: 'rgba(0,0,0,0.5)', padding: '2px', borderRadius: '4px' }}>{j}</div>
              </Html>
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
};

const CameraLogger = () => {
  const { camera } = useThree();
  return (
    <mesh visible={false} onClick={() => {
      const p = camera.position;
      const d = new THREE.Vector3();
      camera.getWorldDirection(d);
      const t = new THREE.Vector3().copy(p).add(d.multiplyScalar(5));
      console.log(`setLookAt(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}, ${t.x.toFixed(2)}, ${t.y.toFixed(2)}, ${t.z.toFixed(2)}, true)`);
    }}>
      <boxGeometry args={[100, 100, 100]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  )
}

const AnimationLoop = ({ walkRef, cameraControlRef, setIsWalking, WALK_SPEED }) => {
  useFrame((state, delta) => {
    if (!cameraControlRef.current || !walkRef.current.active) return;
    if (delta > 0.1) return;
    
    const anim = walkRef.current;
    const camera = cameraControlRef.current.camera;
    const currentPos = camera.position.clone();

    if (anim.isTurning && anim.destLookAt) {
      const currentTargetPos = new THREE.Vector3();
      cameraControlRef.current.getTarget(currentTargetPos);

      const currentLookDir = new THREE.Vector3().subVectors(currentTargetPos, currentPos).normalize();
      const desiredLookDir = new THREE.Vector3().subVectors(anim.destLookAt, currentPos).normalize();

      currentLookDir.lerp(desiredLookDir, delta * 1.2).normalize();
      const lookTarget = currentPos.clone().add(currentLookDir.multiplyScalar(0.1));

      cameraControlRef.current.setLookAt(
        currentPos.x, currentPos.y, currentPos.z, 
        lookTarget.x, lookTarget.y, lookTarget.z, 
        false
      );

      if (currentLookDir.angleTo(desiredLookDir) < 0.05) {
        anim.active = false;
        anim.isTurning = false;
        if (setIsWalking) setIsWalking(false);
      }
      return; 
    }

    if (!anim.currentVelocity) {
      const currentTarget = new THREE.Vector3();
      cameraControlRef.current.getTarget(currentTarget);
      const initialDir = new THREE.Vector3().subVectors(currentTarget, currentPos).normalize();
      if (initialDir.lengthSq() === 0) initialDir.set(0, 0, -1);
      anim.currentVelocity = initialDir.multiplyScalar(WALK_SPEED * 0.5); 
    }

    const targetPos = anim.pathQueue[0];
    const distToTarget = currentPos.distanceTo(targetPos);
    const isLastPoint = anim.pathQueue.length === 1;
    const hitRadius = isLastPoint ? 0.05 : 0.8; 

    if (distToTarget < hitRadius) {
      anim.pathQueue.shift(); 
      if (anim.pathQueue.length === 0) {
        if (anim.destLookAt) {
          anim.isTurning = true;
          cameraControlRef.current.setPosition(targetPos.x, targetPos.y, targetPos.z, false);
        } else {
          anim.active = false;
          anim.currentVelocity = null; 
          if (setIsWalking) setIsWalking(false);

          const currentTargetPos = new THREE.Vector3();
          cameraControlRef.current.getTarget(currentTargetPos);
          const currentLookDir = new THREE.Vector3().subVectors(currentTargetPos, currentPos).normalize();
          const finalLookTarget = targetPos.clone().add(currentLookDir.multiplyScalar(0.1));

          cameraControlRef.current.setLookAt(targetPos.x, targetPos.y, targetPos.z, finalLookTarget.x, finalLookTarget.y, finalLookTarget.z, false);
        }
        return;
      }
    }

    const nextTarget = anim.pathQueue[0];
    const desiredDir = new THREE.Vector3().subVectors(nextTarget, currentPos).normalize();
    const desiredVelocity = desiredDir.multiplyScalar(WALK_SPEED);

    anim.currentVelocity.lerp(desiredVelocity, Math.min(delta * 2.0, 1));
    const nextPos = currentPos.clone().add(anim.currentVelocity.clone().multiplyScalar(delta));

    const currentTargetPos = new THREE.Vector3();
    cameraControlRef.current.getTarget(currentTargetPos);
    const currentLookDir = new THREE.Vector3().subVectors(currentTargetPos, currentPos).normalize();
    const moveDir = anim.currentVelocity.clone().normalize();

    currentLookDir.lerp(moveDir, Math.min(delta * 2.5, 1)).normalize();
    const lookTarget = nextPos.clone().add(currentLookDir.multiplyScalar(0.1));

    cameraControlRef.current.setLookAt(nextPos.x, nextPos.y, nextPos.z, lookTarget.x, lookTarget.y, lookTarget.z, false);
  });
  return null;
};

const CollisionMesh = React.forwardRef(({ url, cameraControlRef, testingMode, rotation = [0, 0, 0], position = [0, 0, 0], onClick }, ref) => {
  const obj = useLoader(OBJLoader, url);
  const lastValidPos = useRef(new THREE.Vector3());
  const lastValidTarget = useRef(new THREE.Vector3());
  const isInitialized = useRef(false);
  const raycaster = useRef(new THREE.Raycaster());

  useEffect(() => {
    if (obj && testingMode) {
      obj.traverse((child) => { if (child.isMesh) child.material = new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true, transparent: true, opacity: 0.3 }); });
    }
  }, [obj, testingMode]);

  useFrame(({ camera }) => {
    if (!cameraControlRef.current || !obj) return;
    const controls = cameraControlRef.current;
    const currentTarget = new THREE.Vector3();
    controls.getTarget(currentTarget);

    if (!isInitialized.current) {
      lastValidPos.current.copy(camera.position);
      lastValidTarget.current.copy(currentTarget);
      isInitialized.current = true;
      return;
    }

    const moveVec = new THREE.Vector3().subVectors(camera.position, lastValidPos.current);
    const distance = moveVec.length();

    if (distance > 0.001) {
      const dir = moveVec.clone().normalize();
      raycaster.current.set(lastValidPos.current, dir);
      const intersects = raycaster.current.intersectObject(obj, true);

      if (intersects.length > 0 && intersects[0].distance <= distance + 0.1) {
        controls.setLookAt(lastValidPos.current.x, lastValidPos.current.y, lastValidPos.current.z, lastValidTarget.current.x, lastValidTarget.current.y, lastValidTarget.current.z, false);
      } else {
        lastValidPos.current.copy(camera.position);
        lastValidTarget.current.copy(currentTarget);
      }
    }
  });

  return <primitive ref={ref} object={obj} visible={testingMode} rotation={rotation} position={position} onClick={onClick} />;
});

export default ModelViewer;